import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import { describeMyDayFailure } from '../writers/my-day-writers';
import { myDayNotificationService } from '../../features/my-day/notification-service';
import type { MyDayBucket, MyDayListRow } from '../database.types';

/**
 * The caller's open follow-ups, already bucketed — M12 / S12-01.
 *
 * UI → THIS HOOK → `list_my_day_items` (ARCHITECTURE §2). **This hook writes
 * nothing, and — just as importantly — it decides nothing.**
 *
 * **THE BUCKET IS THE SERVER'S ANSWER, NOT THIS FILE'S.** Overdue / Today /
 * Upcoming are computed in SQL against the canonical `app_user.timezone`
 * (D-009, INV-T17). There is deliberately no date comparison anywhere in the
 * client: a second implementation would be a second answer to the same
 * question, and the two would disagree at midnight — which is exactly the hour
 * that matters for a thing called "today".
 *
 * AUTHORIZATION IS RLS. `my_day_item_select_own` matches only the caller's own
 * rows under forced row-level security.
 *
 * **A FAILED READ IS NEVER REPORTED AS AN EMPTY DAY.** The reader raises rather
 * than returning nothing when it has no canonical timezone to bucket against
 * (CL006, the `search_captures` precedent), because "you have nothing due" and
 * "Crewlink cannot tell you what is due" look identical on screen and mean
 * opposite things — and the first one is the one that gets believed.
 */
export type MyDayStatus = 'loading' | 'ready' | 'failed';

export interface MyDayState {
  status: MyDayStatus;
  /** Every open item, in server order, each carrying its bucket. */
  items: readonly MyDayListRow[];
  error: string | null;
  reload: () => Promise<void>;
}

/** Group without re-deciding: the bucket on each row is the authority. */
export function groupByBucket(
  items: readonly MyDayListRow[],
): Record<MyDayBucket, readonly MyDayListRow[]> {
  return {
    overdue: items.filter((item) => item.bucket === 'overdue'),
    today: items.filter((item) => item.bucket === 'today'),
    upcoming: items.filter((item) => item.bucket === 'upcoming'),
    // WD-1: resting items, as the SERVER classified them. The client never
    // compares `snoozed_until` to its own clock — that would be the second
    // implementation this comment block forbids, wearing a new column.
    snoozed: items.filter((item) => item.bucket === 'snoozed'),
  };
}

export function useMyDay(): MyDayState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<MyDayStatus>(client ? 'loading' : 'failed');
  const [items, setItems] = useState<readonly MyDayListRow[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  /** A read can finish after the screen is gone. Same guard as every hook here. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fetchItems = useCallback(async (): Promise<
    { kind: 'ready'; rows: MyDayListRow[] } | { kind: 'failed'; message: string }
  > => {
    if (!client) return { kind: 'failed', message: 'Crewlink is not connected to a server yet.' };
    try {
      const { data, error: queryError } = await client.rpc('list_my_day_items');
      if (queryError) {
        reportError(queryError, 'myDay.list');
        // The refusal carries WHICH refusal it was — a missing time zone is a
        // thing Brandon can actually go and fix, and saying so beats a shrug.
        return { kind: 'failed', message: describeMyDayFailure(queryError) };
      }
      return { kind: 'ready', rows: data ?? [] };
    } catch (caught) {
      // A throw is a failed read, not an unhandled rejection: this runs from an
      // effect through `void load()`.
      reportError(caught, 'myDay.list');
      return { kind: 'failed', message: describeMyDayFailure(caught) };
    }
  }, [client]);

  const apply = useCallback(
    (result: { kind: 'ready'; rows: MyDayListRow[] } | { kind: 'failed'; message: string }) => {
      if (!alive.current) return;
      if (result.kind === 'failed') {
        setItems([]);
        setError(result.message);
        setStatus('failed');
        return;
      }
      setItems(result.rows);
      setError(null);
      setStatus('ready');
      // **WD-3: EVERY SUCCESSFUL AUTHORITATIVE READ RECONCILES THE DEVICE
      // SCHEDULE.** The screens re-read after every mutation, so this ONE
      // wire covers create, edit, reschedule, done, dismiss, snooze, wake,
      // recurring advance, skip, end, resume and undo — with no scattered
      // per-action scheduling calls to drift. Never prompts (mayPrompt is
      // for the moment a person makes a timed follow-up matter), never
      // blocks the read, and is a no-op off native.
      void myDayNotificationService().runWith(result.rows);
    },
    [],
  );

  /** A superseded read finishes without writing — the house generation ticket. */
  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchItems();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchItems, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { status, items, error, reload };
}
